import { Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { themeLocaleMap } from './constants/styles';
import { useLocale } from './contexts/locale.context';
import { SnackbarProvider } from './contexts/snackbar.context';
import { getAppPalette } from './theme/appPalette';
import { dateLocaleMap } from './utils/dates';
import lazyWithRetry from './utils/lazyWithRefresh';
import { useLocalStorage } from './utils/useLocalStorage';

const QR = lazyWithRetry(() =>
  import('./QR/QR').then((module) => ({
    default: module.default,
  }))
);

const AppRouter = lazyWithRetry(() =>
  import('./AppRouter').then((module) => ({
    default: module.default,
  }))
);

const ThemedShell = () => {
  const { lang } = useLocale();
  const { pathname } = useLocation();
  const [darkMode] = useLocalStorage<string>('darkMode');
  const isQr = pathname.startsWith('/qr');

  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: getAppPalette(Boolean(darkMode)),
          shape: {
            borderRadius: 8,
          },
        },
        themeLocaleMap[lang]
      ),
    [darkMode, lang]
  );

  useEffect(() => {
    if (isQr || typeof window !== 'object' || typeof window.document?.body?.style !== 'object') {
      return;
    }

    window.document.body.style.backgroundColor = theme.palette.background.default;
  }, [isQr, theme.palette.background.default]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
          bgcolor: isQr ? 'transparent' : 'background.default',
          color: isQr ? undefined : 'text.primary',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocaleMap[lang]}>
            <SnackbarProvider>
              <SWRConfig
                value={{
                  fetcher: (resource, init) => fetch(resource, init).then((res) => res.json()),
                  shouldRetryOnError: false,
                  revalidateOnFocus: false,
                }}
              >
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/qr/:cafeSlug/*" element={<QR />} />
                    <Route path="*" element={<AppRouter />} />
                  </Routes>
                </Suspense>
              </SWRConfig>
            </SnackbarProvider>
          </LocalizationProvider>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const RootRouter = () => (
  <Router>
    <ThemedShell />
  </Router>
);

export default RootRouter;
